import { useEffect, useState } from "react";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import { listQaPlans, listTestCases, type QaPlan, type TestCase } from "../api/delivery.ts";

const STATUS_COLORS: Record<string, string> = {
  DRAFT: "bg-gray-700 text-gray-300",
  ACTIVE: "bg-yellow-900/50 text-yellow-300",
  RELEASED: "bg-green-900/50 text-green-300",
  PASSED: "bg-green-900/50 text-green-300",
  FAILED: "bg-red-900/30 text-red-400",
  BLOCKED: "bg-orange-900/50 text-orange-300",
};

export function DeliveryQaPage() {
  const { token } = useAuth();
  const [plans, setPlans] = useState<QaPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<QaPlan | null>(null);
  const [testCases, setTestCases] = useState<{ items: TestCase[]; total: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token == null) return;
    setLoading(true);
    listQaPlans(token)
      .then(setPlans)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Failed"))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (token == null || selectedPlan == null) return;
    listTestCases(token, { qaPlanId: selectedPlan.id })
      .then(setTestCases)
      .catch(() => setTestCases(null));
  }, [token, selectedPlan]);

  return (
    <Layout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-white">QA & Testing</h1>
        {loading && <p className="text-gray-400">Loading…</p>}
        {error != null && <p className="text-red-400">{error}</p>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <h2 className="text-gray-400 text-sm font-medium uppercase tracking-wide">QA Plans</h2>
            {plans.map((plan) => (
              <button key={plan.id} onClick={() => setSelectedPlan(plan)} className={`w-full text-left bg-gray-800/50 border rounded-lg p-3 transition-colors ${selectedPlan?.id === plan.id ? "border-blue-500" : "border-gray-700 hover:border-gray-600"}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-white text-sm font-medium">{plan.title}</p>
                    <p className="text-gray-500 text-xs mt-0.5">{plan.releaseChecklist.length} checklist items</p>
                  </div>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${STATUS_COLORS[plan.status] ?? "bg-gray-700 text-gray-300"}`}>{plan.status}</span>
                </div>
              </button>
            ))}
            {plans.length === 0 && !loading && <p className="text-gray-400 text-sm">No QA plans.</p>}
          </div>

          <div className="md:col-span-2 space-y-4">
            {selectedPlan != null && (
              <>
                <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-3">Release Checklist</h3>
                  <div className="space-y-2">
                    {selectedPlan.releaseChecklist.map((item, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className={`w-4 h-4 rounded-sm border flex items-center justify-center text-xs ${item.done ? "bg-green-600 border-green-600 text-white" : "border-gray-600"}`}>{item.done ? "✓" : ""}</span>
                        <span className={`text-sm ${item.done ? "text-gray-500 line-through" : "text-gray-300"}`}>{item.item}</span>
                      </div>
                    ))}
                    {selectedPlan.releaseChecklist.length === 0 && <p className="text-gray-500 text-sm">No checklist items.</p>}
                  </div>
                </div>

                {testCases != null && (
                  <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                    <h3 className="text-white font-semibold mb-3">Test Cases ({testCases.total})</h3>
                    <div className="space-y-2">
                      {testCases.items.map((tc) => (
                        <div key={tc.id} className="border border-gray-700/50 rounded p-3">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">{tc.type}</span>
                                <span className={`text-xs px-1.5 py-0.5 rounded ${STATUS_COLORS[tc.status] ?? "bg-gray-700 text-gray-300"}`}>{tc.status}</span>
                              </div>
                              <p className="text-white text-sm mt-0.5">{tc.title}</p>
                              {tc.description && <p className="text-gray-400 text-xs mt-1">{tc.description}</p>}
                            </div>
                            {tc.generatedByAgent && <span className="text-xs text-blue-400 shrink-0">AI</span>}
                          </div>
                          {tc.steps != null && tc.steps.length > 0 && (
                            <ol className="mt-2 space-y-0.5 text-xs text-gray-500 list-decimal list-inside">
                              {tc.steps.map((s, i) => <li key={i}>{s}</li>)}
                            </ol>
                          )}
                        </div>
                      ))}
                      {testCases.items.length === 0 && <p className="text-gray-500 text-sm">No test cases.</p>}
                    </div>
                  </div>
                )}
              </>
            )}
            {selectedPlan == null && <div className="bg-gray-800/30 border border-gray-700/50 rounded-lg p-8 text-center"><p className="text-gray-500">Select a QA plan to view details</p></div>}
          </div>
        </div>
      </div>
    </Layout>
  );
}
